import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  LightMode,
  Text,
} from "@chakra-ui/react"
import { SingleDatepicker } from "chakra-dayzed-datepicker"

import CalendarIcon from "components/icons/calendar_icon"
import XCircleIcon from "components/icons/xcircle_icon"

type FormInputProps = {
  id: string | undefined
  placeholder: string | undefined
  label: string
  error: string | undefined
  selectedDate: Date | undefined
  setSelectedDate: (dates: Date | undefined) => void
  minDate?: Date | undefined
  maxDate?: Date | undefined
  isShowClearIcon?: boolean
  disabled?: boolean
  borders?: string
  DateFormat?: string
}
const MONTH_NAMES_DEFAULT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]
const DAY_NAMES_DEFAULT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const DATE_FORMAT_DEFAULT = "MM-dd-yyyy"

const DatePicker = ({
  borders,
  id,
  label,
  placeholder,
  error,
  selectedDate,
  setSelectedDate,
  minDate,
  maxDate,
  isShowClearIcon = false,
  disabled,
  ...rest
}: FormInputProps) => {
  const isFirefox =
    typeof window !== "undefined" &&
    navigator.userAgent.toLowerCase()?.includes("firefox")
  return (
    <FormControl isInvalid={!!error}>
      {label && (
        <FormLabel htmlFor={id}>
          <Text size='sm' fontWeight='500'>
            {label}
          </Text>
        </FormLabel>
      )}
      <InputGroup>
        <InputLeftElement pl={{ base: "10px", md: "20px" }}>
          <CalendarIcon />
        </InputLeftElement>
        <LightMode>
          <SingleDatepicker
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            date={selectedDate}
            onDateChange={setSelectedDate}
            configs={{
              dateFormat: DATE_FORMAT_DEFAULT,
              monthNames: MONTH_NAMES_DEFAULT,
              dayNames: DAY_NAMES_DEFAULT,
            }}
            propsConfigs={{
              inputProps: {
                width: "100%",
                bg: "white",
                fontSize: "sm",
                fontWeight: "medium",
                border: "1px solid #E1E1E1",
                pl: { base: "40px", md: "50px" },
                borderRadius: borders || "full",
                placeholder,
                _placeholder: {
                  color: "black",
                },
                _focus: {
                  borderColor: "secondaryColor",
                  outline: "1.5px solid",
                  outlineOffset: "0px",
                  outlineColor: "secondaryColor",
                },
                _hover: {},
              },
              dayOfMonthBtnProps: {
                defaultBtnProps: {
                  p: { base: "6px 12px" },
                },
              },
              dateNavBtnProps: isFirefox
                ? {
                    // Prevent focus moving to the navigation buttons to prevent popover closing
                    // when they become disabled (especially in Firefox)
                    onBlur: (e) => e.stopPropagation(),
                  }
                : {},
            }}
            {...rest}
          />
        </LightMode>
        {isShowClearIcon && selectedDate && (
          <InputRightElement pr='5px'>
            <IconButton
              onClick={() => setSelectedDate(undefined)}
              aria-label='clear input'
              variant='ghost'
              icon={<XCircleIcon width='20px' height='20px' />}
            />
          </InputRightElement>
        )}
      </InputGroup>
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  )
}

export default DatePicker
